import { formatHours, formatMoney, formatMonth, formatPercent } from '../../lib/format';
import { MARGIN_BANDS } from './components';
import type { MonthBreakdown, AgreementCore, AgreementMetrics, UnbilledWork } from './api';

/**
 * THE verdict.
 *
 * Every number on the agreement page is a fact; none of them is an answer. A
 * green 55.1% margin sitting beside a red −465 h asks the reader to arbitrate
 * between two contradictory signals, and the page used to print −606.6% and
 * expect them to do the arithmetic. This module does the arbitration once, in
 * one place, and says it in a sentence:
 *
 *   AT RISK — 47% over the hours budget, but still 55.1% margin.
 *             Crossed budget May 2026; burning ~154 h/mo.
 *   LOSING MONEY — $36,395 below the $250/mo fee over 20 months.
 *
 * It reads ONLY the server's `metrics` + `byMonth`, never re-deriving revenue,
 * cost, margin or burn (see agreement-metrics.service.ts — that service owns
 * them). Everything here is composition and English.
 */

export type VerdictTone = 'good' | 'watch' | 'risk' | 'bad' | 'neutral';

/** Stable identity of a verdict — what sorts, filters and counts key off. */
export type VerdictKey = 'losing' | 'risk' | 'thin' | 'watch' | 'healthy' | 'internal' | 'none';

export interface VerdictClass {
  key: VerdictKey;
  tone: VerdictTone;
  /** Two or three words, upper-case in the chip. */
  label: string;
}

export interface Verdict extends VerdictClass {
  /** One sentence that explains the label with the numbers behind it. */
  reason: string;
}

/** How close to the budget earns a warning rather than a clean bill of health. */
const WATCH_AT = 0.75;

/**
 * The health figure a verdict is allowed to judge on.
 *
 * A period-scoped burn is ONE MONTH and is never the verdict; its lifetime
 * companion is. (See BudgetBar — the same rule, enforced twice on purpose.) A
 * period burn with no lifetime figure to fall back on judges nothing at all
 * rather than sentencing an engagement on twelve days.
 */
function healthBurn(metrics: AgreementMetrics) {
  const burn = metrics.burn;
  if (!burn) return null;
  return burn.lifetime ?? (burn.periodMonth ? null : burn);
}

/**
 * THE classification — tone + label from the server's `metrics` alone.
 *
 * Split out of `agreementVerdict` so the agreements LIST can print the very pill
 * the detail page prints without loading a month-by-month breakdown. The list
 * used to show no verdict at all: eighteen rows of raw burn whose meaning
 * changed per row, and six rows whose only health signal was a ghost "Set
 * budget" button. Because both screens now come through this one function they
 * cannot disagree — the sentence in `agreementVerdict` is the same branch, with
 * the numbers spelled out.
 */
export function verdictClass(metrics: AgreementMetrics): VerdictClass {
  const health = healthBurn(metrics);

  if (metrics.hours === 0 && metrics.invoiced === 0) {
    return { key: 'none', tone: 'neutral', label: 'Not started' };
  }
  if (metrics.basis === 'NON_BILLABLE') {
    const over = metrics.burn != null && metrics.burn.pct >= 1;
    return { key: 'internal', tone: over ? 'watch' : 'neutral', label: 'Internal' };
  }
  if (metrics.margin != null && metrics.margin < 0) {
    return { key: 'losing', tone: 'bad', label: 'Losing money' };
  }
  if (health != null && health.pct >= 1) {
    return { key: 'risk', tone: 'risk', label: 'At risk' };
  }
  if (metrics.margin != null && metrics.margin < MARGIN_BANDS.thin) {
    return { key: 'thin', tone: 'watch', label: 'Thin margin' };
  }
  if (health != null && health.pct >= WATCH_AT) {
    return { key: 'watch', tone: 'watch', label: 'Watch' };
  }
  return { key: 'healthy', tone: 'good', label: 'Healthy' };
}

/**
 * Triage order: worst first. This is what the agreements list sorts on by
 * default, because "biggest revenue first" answers a question nobody asks on
 * arrival — the screen you live in should open on the work that is on fire.
 */
export const VERDICT_SEVERITY: Record<VerdictKey, number> = {
  losing: 6,
  risk: 5,
  thin: 4,
  watch: 3,
  healthy: 2,
  none: 1,
  internal: 0,
};

/** Everything at or above THIN is a problem someone has to act on. */
export const ATTENTION_AT = VERDICT_SEVERITY.thin;

export function needsAttention(metrics: AgreementMetrics): boolean {
  return VERDICT_SEVERITY[verdictClass(metrics).key] >= ATTENTION_AT;
}

/**
 * The row-level explanation, composed from the metrics on the row itself.
 *
 * `agreementVerdict` writes a richer sentence ("Crossed budget May 2026; burning
 * ~154 h/mo") because the detail page has the month-by-month series to say it.
 * A list row does not, so it says the same thing with the figures it does have
 * rather than showing a pill with no reasoning attached.
 */
export function verdictSummary(metrics: AgreementMetrics): string {
  const cls = verdictClass(metrics);
  const health = healthBurn(metrics);
  const hoursBurn = metrics.burn?.basis === 'HOURS';
  const parts: string[] = [];
  if (health != null) {
    parts.push(
      `${pct(health.pct)} of the ${
        hoursBurn ? `${formatHours(health.budget)} h budget` : `${money(health.budget)} fee accrued to date`
      } used`,
    );
  }
  if (metrics.margin != null) {
    parts.push(`${formatPercent(metrics.margin, { decimals: 1 })} margin`);
  }
  if (parts.length === 0) {
    parts.push(`${formatHours(metrics.hours, { unit: true })} tracked, ${money(metrics.cost)} of cost`);
  }
  return `${cls.label.toUpperCase()} — ${parts.join(', ')}. Open the agreement for the full reasoning.`;
}

/**
 * How bad, WITHIN the band — so the three loss-making hosting agreements come
 * back −606.6%, −178.2%, −64.2% rather than in whatever order they were
 * fetched, and two over-budget agreements order 147% before 107%.
 */
function badness(metrics: AgreementMetrics, key: VerdictKey): number {
  const health = healthBurn(metrics);
  switch (key) {
    case 'losing':
      return (metrics.margin ?? 0) * -100;
    case 'risk':
      return ((health?.pct ?? 1) - 1) * 100;
    case 'thin':
      return (MARGIN_BANDS.thin - (metrics.margin ?? 0)) * 1000;
    case 'watch':
      return (health?.pct ?? 0) * 100;
    case 'internal':
      return (metrics.burn?.pct ?? 0) * 100;
    default:
      return 0;
  }
}

/**
 * One number per agreement: band, then severity inside the band, then size.
 * Sorting descending puts the portfolio's worst problem in row 1 and internal
 * overhead at the bottom.
 */
export function verdictScore(metrics: AgreementMetrics): number {
  const { key } = verdictClass(metrics);
  const band = VERDICT_SEVERITY[key];
  const bad = Math.max(0, Math.min(999, Math.round(badness(metrics, key))));
  const size = Math.max(0, Math.min(999_999, Math.round(metrics.revenue || metrics.cost)));
  return band * 1e9 + bad * 1e6 + size;
}

const money = (v: number) => formatMoney(v, { whole: true });
const pct = (v: number, decimals = 0) => formatPercent(v, { decimals });

/**
 * True when `month` ('YYYY-MM') is the calendar month we are living through —
 * i.e. its figures are a fraction of a month and must never be read as a rate.
 */
export function isPartialMonth(month: string, now = new Date()): boolean {
  const current = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  if (month !== current) return false;
  const daysInMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
  return now.getDate() < daysInMonth;
}

export interface PartialPeriod {
  month: string;
  label: string;
  dayOfMonth: number;
  daysInMonth: number;
}

/** The trailing month of a series when it is still in progress, else null. */
export function partialPeriod(byMonth: MonthBreakdown[], now = new Date()): PartialPeriod | null {
  const last = byMonth[byMonth.length - 1];
  if (!last || !isPartialMonth(last.month, now)) return null;
  return {
    month: last.month,
    label: formatMonth(`${last.month}-01`),
    dayOfMonth: now.getDate(),
    daysInMonth: new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate(),
  };
}

/**
 * Monthly pace over the last complete months — the partial current month is
 * excluded, because averaging 12 days of August into a rate understates the
 * burn by two thirds and makes every projection optimistic.
 */
function pace(byMonth: MonthBreakdown[], pick: (m: MonthBreakdown) => number, now = new Date()) {
  const complete = byMonth.filter((m) => !isPartialMonth(m.month, now));
  const tail = complete.slice(-3);
  if (tail.length === 0) return null;
  return tail.reduce((s, m) => s + pick(m), 0) / tail.length;
}

/** First month whose cumulative consumption reached the budget. */
function crossedMonth(
  byMonth: MonthBreakdown[],
  pick: (m: MonthBreakdown) => number,
  budget: number,
): string | null {
  const hit = byMonth.find((m) => pick(m) >= budget);
  return hit ? formatMonth(`${hit.month}-01`) : null;
}

function marginClause(metrics: AgreementMetrics): string | null {
  if (metrics.margin == null) return null;
  return `${formatPercent(metrics.margin, { decimals: 1 })} margin`;
}

export function agreementVerdict(
  agreement: AgreementCore,
  metrics: AgreementMetrics,
  byMonth: MonthBreakdown[],
  now = new Date(),
): Verdict {
  const burn = metrics.burn;
  const hoursBurn = burn?.basis === 'HOURS';
  const health = healthBurn(metrics);
  const healthIsLifetime = burn?.lifetime != null;
  // The label and tone are decided in exactly one place, shared with the
  // agreements list. Everything below only writes the sentence for that branch.
  const cls = verdictClass(metrics);

  // --- nothing to judge ----------------------------------------------------
  if (cls.key === 'none') {
    return {
      ...cls,
      reason: 'No time tracked and nothing invoiced yet — there is nothing to judge.',
    };
  }

  if (cls.key === 'internal') {
    const cost = money(metrics.cost);
    const budgetPart =
      burn != null
        ? ` — ${pct(burn.pct)} of the ${
            burn.basis === 'HOURS' ? `${formatHours(burn.budget)} h` : money(burn.budget)
          } internal budget`
        : '';
    return {
      ...cls,
      reason: `Non-billable: ${cost} of cost over ${formatHours(metrics.hours, { unit: true })}${budgetPart}.`,
    };
  }

  // --- losing money --------------------------------------------------------
  if (cls.key === 'losing') {
    const loss = metrics.cost - metrics.revenue;
    let reason: string;
    if (metrics.basis === 'MONTHLY_FEE' && agreement.budgetAmount != null) {
      const months = metrics.feeMonths ?? 0;
      reason = `${money(loss)} below the ${money(agreement.budgetAmount)}/mo fee over ${months} month${
        months === 1 ? '' : 's'
      }.`;
    } else if (metrics.basis === 'FIXED_FEE' && agreement.budgetAmount != null) {
      reason = `Delivery cost is ${money(loss)} over the ${money(agreement.budgetAmount)} fixed fee.`;
    } else {
      reason = `${money(metrics.cost)} of cost against ${money(metrics.revenue)} of revenue — ${money(loss)} under water.`;
    }
    const monthly = pace(byMonth, (m) => m.cost - m.invoiced, now);
    if (metrics.basis !== 'HOURLY' && monthly != null && monthly > 0) {
      reason += ` Losing about ${money(monthly)} a month at the current pace.`;
    }
    return { ...cls, reason };
  }

  // --- over budget ---------------------------------------------------------
  if (cls.key === 'risk' && health != null) {
    const over = pct(health.pct - 1);
    const noun = hoursBurn
      ? 'hours budget'
      : healthIsLifetime
        ? 'fee accrued to date'
        : metrics.basis === 'HOURLY'
          ? 'budget'
          : 'fee';
    const margin = marginClause(metrics);
    let reason = margin
      ? `${over} over the ${noun}, but still ${margin}.`
      : `${over} over the ${noun}.`;
    const detail: string[] = [];
    if (hoursBurn && agreement.budgetHours != null) {
      const crossed = crossedMonth(byMonth, (m) => m.cumulativeHours, agreement.budgetHours);
      if (crossed) detail.push(`Crossed budget ${crossed}`);
      const monthly = pace(byMonth, (m) => m.hours, now);
      if (monthly != null && monthly > 0) {
        detail.push(`burning ~${formatHours(Math.round(monthly), { unit: true })}/mo`);
      }
    } else {
      const monthly = pace(byMonth, (m) => m.cost, now);
      if (monthly != null && monthly > 0) detail.push(`spending ~${money(monthly)}/mo`);
    }
    if (detail.length > 0) reason += ` ${detail.join('; ')}.`;
    return { ...cls, reason };
  }

  // --- thin margin ---------------------------------------------------------
  if (cls.key === 'thin') {
    const budgetPart =
      health != null
        ? ` ${pct(health.pct)} of the ${hoursBurn ? 'hours budget' : 'fee'} is used.`
        : '';
    return {
      ...cls,
      reason: `${formatPercent(metrics.margin ?? 0, { decimals: 1 })} on ${money(metrics.revenue)} of revenue — ${money(
        metrics.cost,
      )} of cost leaves little room.${budgetPart}`,
    };
  }

  // --- approaching the budget ---------------------------------------------
  if (cls.key === 'watch' && health != null) {
    const margin = marginClause(metrics);
    let reason = `${pct(health.pct)} of the ${
      hoursBurn ? `${formatHours(health.budget)} h budget` : `${money(health.budget)} fee`
    } used${margin ? `, ${margin}` : ''}.`;
    if (hoursBurn && agreement.budgetHours != null) {
      const monthly = pace(byMonth, (m) => m.hours, now);
      if (monthly != null && monthly > 0) {
        const left = Math.max(0, agreement.budgetHours - metrics.hours);
        reason += ` At ~${formatHours(Math.round(monthly), { unit: true })}/mo that is about ${Math.max(
          1,
          Math.round(left / monthly),
        )} month${Math.round(left / monthly) === 1 ? '' : 's'} of headroom.`;
      }
    }
    return { ...cls, reason };
  }

  // --- healthy -------------------------------------------------------------
  const margin = marginClause(metrics);
  const parts: string[] = [];
  if (margin) parts.push(`${margin} on ${money(metrics.revenue)} of revenue`);
  if (health != null) {
    parts.push(
      `${pct(health.pct)} of the ${
        hoursBurn ? `${formatHours(health.budget)} h budget` : `${money(health.budget)} fee`
      } used`,
    );
  } else if (!margin) {
    parts.push(`${formatHours(metrics.hours, { unit: true })} tracked, ${money(metrics.cost)} of cost`);
  }
  return { ...cls, reason: `${parts.join('; ')}.` };
}

// ---------------------------------------------------------------------------
// The primary action — every agreement offers a next step, not just the rich ones
// ---------------------------------------------------------------------------

/**
 * The action slot used to fire only when there was unbilled money to collect.
 * So the single worst agreement in the portfolio — LOSING MONEY, bleeding
 * ~$2,036/month — offered nothing at all but Edit and Archive, while a healthy
 * agreement with $8,080 uninvoiced got a primary button. The button now follows
 * the verdict: bill it, reprice it, look at the rates, or set the budget that
 * would have caught this.
 *
 * `kind` decides what the page does with it (navigate, or open the edit modal);
 * everything else is copy composed from server figures.
 */
export type AgreementActionKind = 'fee' | 'rates' | 'budget' | 'scope';

export interface AgreementAction {
  kind: AgreementActionKind;
  label: string;
  /** Present for navigating actions; absent ones open the edit modal. */
  to?: string;
  title: string;
}

export function agreementAction(
  agreement: AgreementCore,
  metrics: AgreementMetrics,
  // `unbilled` kept in the signature: callers still pass it, and the figure
  // stays informational elsewhere — only the ACTION is gone.
  _unbilled: UnbilledWork,
  byMonth: MonthBreakdown[],
  now = new Date(),
): AgreementAction | null {
  // The invoice rung is GONE (Jesse, 2026-08-22): this shop raises hourly
  // invoices by AMOUNT — Pilot Tools shows $74,250 invoiced against $52,101 of
  // billable value — so a button proposing to bill specific hours proposes an
  // action they never take. The verdict-driven rungs below (fee, rates,
  // budget, scope) are the real levers and now surface instead. Support
  // OVERAGE billing on the client page is a different flow and stays.

  const thin = metrics.margin != null && metrics.margin < MARGIN_BANDS.thin;

  // 2. A fee that does not cover delivery is a pricing problem — and we can say
  //    what the fee would have to be.
  if (thin && agreement.budgetAmount != null && agreement.budgetAmount > 0) {
    if (metrics.basis === 'MONTHLY_FEE') {
      const months = metrics.feeMonths ?? 0;
      const breakEven = months > 0 ? metrics.cost / months : null;
      return {
        kind: 'fee',
        label: `Reprice the ${money(agreement.budgetAmount)}/mo fee`,
        title: breakEven
          ? `Delivery has cost about ${money(breakEven)}/month over ${months} months against a ${money(
              agreement.budgetAmount,
            )}/month fee. Opens the agreement settings.`
          : 'Opens the agreement settings, where the monthly fee lives.',
      };
    }
    if (metrics.basis === 'FIXED_FEE') {
      return {
        kind: 'fee',
        label: `Reprice the ${money(agreement.budgetAmount)} fee`,
        title: `Delivery has cost ${money(metrics.cost)} against a ${money(
          agreement.budgetAmount,
        )} fixed fee. Opens the agreement settings.`,
      };
    }
  }

  // 3. Hourly work with a thin margin is a rate problem, and rates are editable
  //    per person on the Team tab.
  if (thin && metrics.basis === 'HOURLY') {
    return {
      kind: 'rates',
      label: 'Review rates',
      to: `/agreements/${agreement.id}?tab=team`,
      title: `${formatPercent(metrics.margin ?? 0, { decimals: 1 })} margin on ${money(
        metrics.revenue,
      )} of revenue. Bill and cost rates are editable per person on the Team tab.`,
    };
  }

  // 4. No budget at all — the thing that would have caught any of the above.
  //
  // Internal work is included. It was excluded, so the one agreement carrying
  // $472,765 of unbilled overhead offered no next step at all in the header
  // while its own BUDGET tile sat there with a "Set a budget" button — the
  // action slot vanished and Edit/Archive slid across the page. Non-billable
  // does not mean unmanaged: an hours budget is exactly how you find out that
  // internal ops is eating a quarter of the studio.
  if (metrics.burn == null) {
    return {
      kind: 'budget',
      label: metrics.basis === 'NON_BILLABLE' ? 'Set an hours budget' : 'Set a budget',
      title:
        metrics.basis === 'NON_BILLABLE'
          ? `Nothing to burn against — ${formatHours(metrics.hours, { unit: true })} and ${money(
              metrics.cost,
            )} of internal cost are being tracked against no ceiling.`
          : 'Nothing to burn against — set hours or an amount and every screen starts tracking it.',
    };
  }

  // 5. Internal work already over its own budget — the hours are the evidence.
  if (metrics.basis === 'NON_BILLABLE' && metrics.burn != null && metrics.burn.pct >= 1) {
    return {
      kind: 'scope',
      label: 'Review the time',
      to: `/agreements/${agreement.id}?tab=time`,
      title: `${pct(metrics.burn.pct)} of the internal budget is used. The time ledger shows who is booking it and on which task.`,
    };
  }

  // 6. Over an hours budget, with nothing to invoice: the scope conversation.
  const burn = metrics.burn;
  if (burn?.basis === 'HOURS' && burn.pct >= 1 && agreement.budgetHours != null) {
    const monthly = pace(byMonth, (m) => m.hours, now);
    const over = burn.used - burn.budget;
    return {
      kind: 'scope',
      label: 'Extend the budget',
      title: `${formatHours(over, { unit: true })} past the ${formatHours(agreement.budgetHours, {
        unit: true,
      })} budget${
        monthly != null && monthly > 0 ? `, still burning ~${formatHours(Math.round(monthly), { unit: true })}/mo` : ''
      }. Opens the agreement settings.`,
    };
  }

  return null;
}
